import React, { useEffect, useState, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as storageService from '../../services/storageService';
import { Hospital, Patient, StaffUser, ScreenSession } from '../../types';
import {
    BarChart3, Megaphone, Key, ArrowLeft, HeartPulse, Building, ChevronDown,
    Plug, Sunrise, Users, Monitor, Activity, ChevronLeft, ChevronRight,
    Clock, Stethoscope, TrendingUp, AlertCircle, CalendarDays, History, MessageSquare, Settings, Lock, Star
} from 'lucide-react';
import { AdminNavbarUser } from '../../components/AdminNavbarUser';
import AnimatedNumber from '../../components/shared/AnimatedNumber';
import BugReportWidget from '../../components/BugReportWidget';

// ── Lazy-load sub-modules (keeps initial bundle fast) ──
const HospitalDashboard = lazy(() => import('../HospitalDashboard'));
const AdminPublicidad   = lazy(() => import('./AdminPublicidad'));
const AdminAccesos      = lazy(() => import('./AdminAccesos'));
const AdminIntegraciones = lazy(() => import('./AdminIntegraciones'));
const HospitalSettings  = lazy(() => import('./HospitalSettings'));
const MarketingDashboard = lazy(() => import('../../components/MarketingDashboard'));

// ═══════════════════════════════════════════════════════════════
// DESIGN TOKENS (Yin-Yang: dark sidebar + light content)
// ═══════════════════════════════════════════════════════════════
const t = {
    bg: 'bg-[#F8FAFC]',
    textMain: 'text-slate-600',
    textWhite: 'text-slate-900',
    cardBg: 'bg-white/70 backdrop-blur-2xl',
    cardBorder: 'border-slate-200/60',
    cardShadow: 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
    sidebarBg: 'bg-gradient-to-b from-[#0B1628] via-[#0F1D35] to-[#0B1628]',
    sidebarBorder: 'border-white/[0.06]',
    sidebarText: 'text-slate-400',
    sidebarHover: 'hover:bg-white/[0.04]',
    sidebarDivider: 'border-white/[0.06]',
};

type TabId = 'resumen' | 'analytics' | 'ad_analytics' | 'ads' | 'calendar' | 'monitors' | 'feedback' | 'accesos' | 'integraciones' | 'settings';

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════
const AdminHub: React.FC = () => {
    const navigate = useNavigate();
    const { hospitalId } = useParams<{ hospitalId: string }>();
    const [hospital, setHospital] = useState<Hospital | null>(null);
    const [loading, setLoading] = useState(true);

    // Data for Resumen tab
    const [patients, setPatients] = useState<Patient[]>([]);
    const [staff, setStaff] = useState<StaffUser[]>([]);
    const [screens, setScreens] = useState<ScreenSession[]>([]);
    
    // Historical Snapshots
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>(''); // '' means today
    const [historicalSnapshot, setHistoricalSnapshot] = useState<any>(null);

    // Active tab & sidebar
    const [activeTab, setActiveTab] = useState<TabId>('resumen');
    const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
        localStorage.getItem('hs_branch_sidebar') !== 'closed'
    );
    useEffect(() => {
        localStorage.setItem('hs_branch_sidebar', isSidebarOpen ? 'open' : 'closed');
    }, [isSidebarOpen]);

    const auth = storageService.getAuthData();
    const targetHospitalId = hospitalId || auth?.hospitalId;
    const effectiveRole = (auth?.role || auth?.staffRole || '') as string;
    const canSeeAdminPanel = !auth?.role || ['SUPER_ADMIN','GROUP_ADMIN','HOSPITAL_ADMIN','FINANCIERO','MARKETING'].includes(effectiveRole);
    const isAdmin = !auth?.role || ['SUPER_ADMIN','GROUP_ADMIN','HOSPITAL_ADMIN'].includes(effectiveRole);
    const isMarketing = effectiveRole === 'MARKETING';

    useEffect(() => {
        const load = async () => {
            if (!auth && !targetHospitalId) { navigate('/login'); return; }
            if (!targetHospitalId) { navigate('/login'); return; }
            const isImpersonating = window.location.search.includes('impersonate=true');
            if (!isImpersonating && targetHospitalId === 'ADMIN') {
                navigate('/admin/dashboard'); return;
            }
            if (!canSeeAdminPanel) { navigate('/staff'); return; }

            const found = await storageService.getHospitalById(targetHospitalId);
            setHospital(found || null);

            // Load Resumen data
            try {
                const [pData, sData, scData, dates] = await Promise.all([
                    storageService.getPatientsByHospital(targetHospitalId),
                    storageService.getStaffByHospital(targetHospitalId),
                    storageService.getScreenSessions(targetHospitalId),
                    storageService.getHospitalSnapshotDates(targetHospitalId),
                ]);
                setPatients(pData);
                setStaff(sData);
                setScreens(scData as ScreenSession[]);
                setAvailableDates(dates);
            } catch { /* non-critical */ }

            setLoading(false);
        };
        load();
    }, [targetHospitalId]);

    // Fetch snapshot if past date selected
    useEffect(() => {
        if (!selectedDate || !targetHospitalId) {
            setHistoricalSnapshot(null);
            return;
        }
        const fetchSnapshot = async () => {
            try {
                const snap = await storageService.getHospitalSnapshot(targetHospitalId, selectedDate);
                setHistoricalSnapshot(snap.metrics);
            } catch (e) {
                console.error(e);
                setHistoricalSnapshot(null);
            }
        };
        fetchSnapshot();
    }, [selectedDate, targetHospitalId]);

    // ── Hooks BEFORE early returns (Rules of Hooks) ──
    const liveAvgSurgeryMin = useMemo(() => {
        const durations: number[] = [];
        patients.forEach(p => {
            const logs = [...(p.logs || [])].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            const start = logs.find(l => l.status === 'SURGERY');
            const end = logs.find(l => l.status === 'RECOVERY') || logs.find(l => l.status === 'EXIT');
            if (start && end) {
                const ms = new Date(end.timestamp).getTime() - new Date(start.timestamp).getTime();
                if (ms > 0 && ms < 24*60*60*1000) durations.push(ms / 60000);
            }
        });
        return durations.length > 0 ? Math.round(durations.reduce((a,b) => a+b, 0) / durations.length) : 0;
    }, [patients]);

    const liveRecentMoves = useMemo(() => {
        const moves: { name: string; status: string; time: string }[] = [];
        patients.forEach(p => {
            (p.logs || []).forEach(log => {
                moves.push({ name: p.name || p.id.slice(0, 6), status: log.status, time: log.timestamp });
            });
        });
        return moves.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).slice(0, 6);
    }, [patients]);

    if (loading) return null;
    if (!hospital) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Hospital no configurado</h1>
                <button onClick={() => navigate('/staff')} className="text-indigo-600 font-bold hover:underline">Volver</button>
            </div>
        );
    }

    const primaryColor = hospital.branding?.primaryColor || '#5B65DC';

    // ── Modules Entitlements ──
    const isSuperAdmin = auth?.role === 'SUPER_ADMIN';
    const hasAdsModule = !hospital?.features || hospital.features.includes('ADS');
    const canSeeAdsModule = isSuperAdmin || hasAdsModule;

    // ── Sidebar Navigation Items ──
    const sidebarSections = [
        {
            label: 'General',
            items: [
                { id: 'resumen', label: 'Resumen', icon: Sunrise, visible: true },
            ]
        },
        {
            label: 'Inteligencia',
            items: [
                { id: 'analytics', label: 'Analíticas Médicas', icon: Activity, visible: !isMarketing },
            ]
        },
        {
            label: 'Marketing',
            items: [
                { id: 'ads', label: 'Anuncios', icon: Megaphone, visible: (isAdmin || isMarketing) && canSeeAdsModule, locked: !hasAdsModule },
                { id: 'ad_analytics', label: 'Métricas', icon: TrendingUp, visible: (isAdmin || isMarketing) && canSeeAdsModule, locked: !hasAdsModule },
                { id: 'feedback', label: 'Satisfacción', icon: Star, visible: isAdmin || isMarketing },
                { id: 'calendar', label: 'Calendario', icon: CalendarDays, visible: isAdmin || isMarketing },
            ]
        },
        {
            label: 'Operaciones',
            items: [
                { id: 'monitors', label: 'Monitores', icon: Monitor, visible: isAdmin && !isMarketing },
                { id: 'accesos', label: 'Accesos', icon: Key, visible: isAdmin && !isMarketing },
                { id: 'integraciones', label: 'Integraciones', icon: Plug, visible: isAdmin && !isMarketing },
                { id: 'settings', label: 'Configuración', icon: Settings, visible: isAdmin && !isMarketing },
            ]
        }
    ];

    const visibleNav = sidebarSections.flatMap(section => 
        section.items.filter(item => item.visible)
    );

    // ── Computations (Live vs Historical) ──
    const isHistorical = !!selectedDate && !!historicalSnapshot;

    const kpiValues = isHistorical ? {
        patientsToday: historicalSnapshot.kpis.patientsToday,
        inProcess: historicalSnapshot.kpis.inProcess,
        activeStaff: historicalSnapshot.kpis.activeStaff,
        onlineScreens: historicalSnapshot.kpis.onlineScreens
    } : {
        patientsToday: patients.filter(p => new Date(p.admissionTime).toDateString() === new Date().toDateString()).length,
        inProcess: patients.filter(p => p.currentStatus !== 'EXIT' && p.currentStatus !== 'DISCHARGED').length,
        activeStaff: staff.filter(s => s.isActive).length,
        onlineScreens: screens.filter(s => s.isOnline).length
    };

    const avgSurgeryMin = isHistorical ? historicalSnapshot.avgSurgeryMin : liveAvgSurgeryMin;
    const recentMoves = isHistorical ? historicalSnapshot.recentMoves : liveRecentMoves;

    const kpis = [
        { label: 'Pacientes ' + (isHistorical ? 'Ese Día' : 'Hoy'), value: kpiValues.patientsToday, icon: Stethoscope, color: '#5B65DC', bg: 'bg-indigo-50', border: 'border-indigo-200' },
        { label: 'En Proceso', value: kpiValues.inProcess, icon: Activity, color: '#10B981', bg: 'bg-emerald-50', border: 'border-emerald-200' },
        { label: 'Staff Activo', value: kpiValues.activeStaff, icon: Users, color: '#8B5CF6', bg: 'bg-violet-50', border: 'border-violet-200' },
        { label: 'Pantallas Online', value: kpiValues.onlineScreens, icon: Monitor, color: '#F59E0B', bg: 'bg-amber-50', border: 'border-amber-200' },
    ];

    const STATUS_LABELS: Record<string, string> = {
        WAITING: 'En Espera', PREPARATION: 'Preparación', ROOM_ENTRY: 'Ingreso Qx',
        ANESTHESIA: 'Anestesia', SURGERY: 'Cirugía', RECOVERY: 'Recuperación',
        EXIT: 'Alta', DISCHARGED: 'Dado de Alta',
    };
    const STATUS_COLORS: Record<string, string> = {
        WAITING: 'bg-slate-100 text-slate-600', PREPARATION: 'bg-blue-50 text-blue-600',
        ROOM_ENTRY: 'bg-indigo-50 text-indigo-600', ANESTHESIA: 'bg-amber-50 text-amber-600',
        SURGERY: 'bg-red-50 text-red-600', RECOVERY: 'bg-emerald-50 text-emerald-600',
        EXIT: 'bg-slate-50 text-slate-500', DISCHARGED: 'bg-slate-50 text-slate-400',
    };

    // ── Loading Fallback ──
    const LoadingFallback = () => (
        <div className="flex items-center justify-center py-32">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin" />
        </div>
    );

    // ── Render Resumen Tab ──
    const renderResumen = () => (
        <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Panel Administrativo</p>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                        Resumen de la Sucursal
                        {isHistorical && <span className="ml-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-700 text-xs tracking-normal align-middle"><History className="w-3.5 h-3.5" /> Archivo Histórico</span>}
                    </h1>
                </div>

                <div className="relative">
                    <select
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="appearance-none pl-10 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all cursor-pointer"
                    >
                        <option value="">Hoy (En Vivo)</option>
                        {availableDates.map(d => (
                            <option key={d} value={d}>
                                {new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </option>
                        ))}
                    </select>
                    <CalendarDays className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500" />
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {kpis.map((kpi, i) => (
                    <div key={i} className={`${t.cardBg} rounded-[1.5rem] p-6 border ${t.cardBorder} ${t.cardShadow} hover:shadow-lg transition-all duration-300 group relative overflow-hidden`}>
                        <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-[40px] opacity-20 pointer-events-none" style={{ backgroundColor: kpi.color }} />
                        <div className={`w-10 h-10 rounded-xl ${kpi.bg} border ${kpi.border} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                            <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
                        </div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{kpi.label}</p>
                        <p className="text-3xl font-black text-slate-900 tracking-tight">
                            <AnimatedNumber value={kpi.value} />
                        </p>
                    </div>
                ))}
            </div>

            {/* Two-column: Surgery KPI + Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className={`${t.cardBg} rounded-[1.5rem] p-6 border ${t.cardBorder} ${t.cardShadow}`}>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-rose-500" />
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-slate-900 tracking-tight">Tiempo Promedio en Cirugía</h3>
                            <p className="text-[10px] text-slate-400 font-medium">Basado en historial de pacientes</p>
                        </div>
                    </div>
                    <div className="flex items-end gap-2">
                        <span className="text-5xl font-black text-slate-900 tracking-tight"><AnimatedNumber value={avgSurgeryMin} /></span>
                        <span className="text-lg font-bold text-slate-400 mb-1">min</span>
                    </div>
                    {avgSurgeryMin === 0 && (
                        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> Sin datos suficientes aún</p>
                    )}
                </div>

                <div className={`${t.cardBg} rounded-[1.5rem] p-6 border ${t.cardBorder} ${t.cardShadow}`}>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                            <Activity className="w-5 h-5 text-indigo-500" />
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-slate-900 tracking-tight">Actividad Reciente</h3>
                            <p className="text-[10px] text-slate-400 font-medium">Últimos movimientos de pacientes</p>
                        </div>
                    </div>
                    {recentMoves.length === 0 ? (
                        <p className="text-sm text-slate-400 py-6 text-center">Sin actividad reciente</p>
                    ) : (
                        <div className="space-y-2.5">
                            {recentMoves.map((mv, i) => (
                                <div key={i} className="flex items-center justify-between py-1.5">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                                            {mv.name.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="text-sm font-bold text-slate-800 truncate">{mv.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_COLORS[mv.status] || 'bg-slate-100 text-slate-500'}`}>
                                            {STATUS_LABELS[mv.status] || mv.status}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-mono">
                                            {new Date(mv.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>



            <div className="mt-12 flex items-center gap-4 text-slate-300 font-bold text-[9px] uppercase tracking-[0.2em] opacity-60">
                <span className="flex items-center gap-1.5"><Building className="w-3.5 h-3.5" /> Estación Verificada</span>
                <span className="w-1 h-1 rounded-full bg-slate-200" />
                <span>Hospital Status v2</span>
            </div>
        </div>
    );

    // ── Render Active Tab Content ──
    const renderContent = () => {
        if (activeTab === 'resumen') return renderResumen();

        return (
            <Suspense fallback={<LoadingFallback />}>
                {activeTab === 'analytics'     && <HospitalDashboard />}
                {['ads', 'ad_analytics', 'feedback', 'calendar', 'monitors', 'staff_sessions'].includes(activeTab) && (
                     <AdminPublicidad activeSubTab={activeTab as any} />
                )}
                {activeTab === 'accesos'       && <AdminAccesos />}
                {activeTab === 'integraciones' && <AdminIntegraciones />}
                {activeTab === 'settings'      && <HospitalSettings handle={hospital?.handle} />}
            </Suspense>
        );
    };

    return (
        <div className={`min-h-screen ${t.bg} font-sans relative overflow-hidden flex flex-col xl:flex-row ${t.textMain}`}>
            {/* Ambient Orbs */}
            <div className="fixed top-0 right-0 w-1/2 h-96 bg-indigo-500/5 blur-[120px] rounded-full pointer-events-none z-0" />
            <div className="fixed bottom-0 left-0 w-1/2 h-96 bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none z-0" />

            {/* ═══════ SIDEBAR ═══════ */}
            <aside className={`hidden xl:flex flex-col ${isSidebarOpen ? 'w-[260px]' : 'w-[72px]'} fixed inset-y-0 left-0 z-40 ${t.sidebarBg} border-r ${t.sidebarBorder} transition-all duration-300 ease-in-out`}>
                {/* Logo */}
                <div className={`px-4 pt-6 pb-5 shrink-0 flex items-center ${isSidebarOpen ? 'gap-3' : 'justify-center'} transition-all border-b ${t.sidebarDivider}`}>
                    {hospital.branding?.logoUrl ? (
                        <img src={hospital.branding.logoUrl} alt="Logo" className={`${isSidebarOpen ? 'w-10 h-10' : 'w-9 h-9'} rounded-xl object-contain flex-shrink-0`} />
                    ) : (
                        <div className={`${isSidebarOpen ? 'w-10 h-10' : 'w-9 h-9'} rounded-xl flex items-center justify-center flex-shrink-0`} style={{ backgroundColor: `${primaryColor}20` }}>
                            <HeartPulse className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>
                    )}
                    {isSidebarOpen && <h1 className="text-[15px] font-black text-white tracking-tight truncate">{hospital.name}</h1>}
                </div>

                {/* Nav Items */}
                <div className="flex-1 overflow-y-auto px-3 pt-4 space-y-0.5 scrollbar-hide">
                    {sidebarSections.map((section, si) => {
                        const visibleItems = section.items.filter(i => i.visible);
                        if (visibleItems.length === 0) return null;
                        
                        return (
                            <React.Fragment key={section.label}>
                                {si > 0 && (
                                    <>
                                        {isSidebarOpen && <div className="pt-4 pb-1.5 px-3"><p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">{section.label}</p></div>}
                                        {!isSidebarOpen && <div className="w-6 h-px bg-white/[0.06] mx-auto my-3" />}
                                    </>
                                )}
                                {si === 0 && isSidebarOpen && <div className="pb-1.5 px-3"><p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">{section.label}</p></div>}
                                
                                {visibleItems.map(item => {
                                    const isActive = activeTab === item.id;
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => setActiveTab(item.id as TabId)}
                                            title={!isSidebarOpen ? item.label : undefined}
                                            className={`w-full relative ${isSidebarOpen ? 'px-3 py-2.5 text-left gap-3' : 'py-2.5 justify-center text-center'} rounded-lg text-[13px] font-semibold transition-all duration-200 flex items-center ${
                                                isActive
                                                    ? 'bg-white/[0.08] text-white border-l-[3px] border-indigo-400'
                                                    : `${t.sidebarText} ${t.sidebarHover} border-l-[3px] border-transparent`
                                            }`}
                                        >
                                            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                                            {isSidebarOpen && <span className="whitespace-nowrap flex-1">{item.label}</span>}
                                            {isSidebarOpen && item.locked && (
                                                <div className="bg-amber-500/10 border border-amber-500/20 text-amber-500 rounded px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest flex-shrink-0" title="Módulo restringido (Visible solo para Super Admin)">LOCKED</div>
                                            )}
                                        </button>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* User + Toggle */}
                <div className={`border-t ${t.sidebarDivider} mt-auto`}>
                    <AdminNavbarUser primaryColor={primaryColor} theme="dark" variant="sidebar" collapsed={!isSidebarOpen} />
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className={`w-full flex items-center ${isSidebarOpen ? 'justify-end px-4' : 'justify-center'} py-3 text-slate-500 hover:text-slate-300 transition-colors`}
                    >
                        {isSidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                </div>
            </aside>

            {/* ═══════ MOBILE HEADER ═══════ */}
            <div className="xl:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-2xl border-b border-slate-200/60">
                <div className="px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/staff')} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm font-black text-slate-800 truncate">{hospital.name}</span>
                    </div>
                    <AdminNavbarUser primaryColor={primaryColor} />
                </div>
                <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
                    {visibleNav.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`px-3 py-1.5 flex items-center gap-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                                activeTab === item.id
                                    ? 'bg-slate-900 text-white'
                                    : 'text-slate-500 bg-slate-100 hover:bg-slate-200'
                            }`}
                        >
                            {item.label}
                            {item.locked && <Lock className="w-3 h-3 text-amber-500" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* ═══════ MAIN CONTENT ═══════ */}
            <main className={`flex-1 ${isSidebarOpen ? 'xl:ml-[260px]' : 'xl:ml-[72px]'} transition-all duration-300 relative z-10 flex flex-col`}>
                <div className="flex-1">
                    {renderContent()}
                </div>
                
                {/* Discrete Bug Report Footer */}
                <div className={`text-center py-4 mt-auto border-t border-slate-200/50`}>
                    <BugReportWidget hospitalName={hospital.name} />
                </div>
            </main>
        </div>
    );
};

export default AdminHub;
